import { useQuery } from "@tanstack/react-query"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs"
import { useState, useEffect } from "react"
import { Button } from "@workspace/web-ui/components/button"
import { linkOptions, useNavigate, useParams } from "@tanstack/react-router"
import { toTitleCase } from "@workspace/utils/string/index"
import { useDebounce } from "~/hooks/use-debounce"
import { PaymentFilters } from "../components/payment-filter"
import PaymentHistoryTable from "../components/payment-history-table"
import { paymentsQueryOptions } from "../../encounters/api/queries/payment"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { getApiProgram } from "~/lib/utils"
import { PaymentFilterDate, PaymentFilterStatus } from "../types"

// Skeleton loader for encounter list
function EncounterListSkeleton() {
    return (
        <div className="flex flex-col gap-4 w-full">
            {[...Array(5)].map((_, i) => (
                <div
                    key={i}
                    className="animate-pulse bg-gray-200 rounded h-24 w-full"
                />
            ))}
        </div>
    );
}

export const paymentsLinkOptions = (program = 'weight-loss', tab = 'all') => linkOptions({
    to: '/dashboard/$program/payments/$tab',
    params: { program, tab }
})

export function PaymentsPage() {
    // const queryClient = getQueryClient()
    const { tab, program } = useParams({ strict: false })
    const navigate = useNavigate()
    const [currentPage, setCurrentPage] = useState(1);
    const [isMobile, setIsMobile] = useState(false);
    const [searchValue, setSearchValue] = useState("")
    const debouncedSearchValue = useDebounce<string>(searchValue, 400);
    const [date, setDate] = useState("");
    const [status, setStatus] = useState("");
    const pageSize = 10;
    const skip = (currentPage - 1) * pageSize;
    const apiProgram = getApiProgram(program ?? "")


    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 640);
        };

        // Check on initial render
        checkScreenSize();

        // Add event listener for window resize
        window.addEventListener("resize", checkScreenSize);

        // Clean up event listener
        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);


    // Fetch only the current page's data
    const { data, isLoading, isFetching } = useQuery(
        paymentsQueryOptions({
            limit: pageSize,
            skip,
            date_filter: date === "all" ? "all" : date as PaymentFilterDate,
            filter: tab === "all" ? undefined : tab,
            status: status === "all" ? "all" : status as PaymentFilterStatus,
            search: debouncedSearchValue,
            program: apiProgram
        })
    );


    // Prefetch the next page!
    // useEffect(() => {
    //     if (!isPlaceholderData && data?.hasMore) {
    //         queryClient.prefetchQuery({
    //             queryKey: encountersKeys.encounters({
    //                 limit: pageSize,
    //                 skip: skip + pageSize,

    //                 type: tab === "all" ? undefined : tab, date_filter: date === "all" ? "all" : date as EncounterFilterDate, filter: filter === "all" ? "all" : filter as EncounterFilter, status: status === "all" ? "all" : status as EncounterFilterStatus, search: debouncedSearchValue
    //             }),
    //             queryFn: () => encountersQueryOptions({
    //                 limit: pageSize,
    //                 skip: skip + pageSize,

    //                 type: tab === "all" ? undefined : tab, date_filter: date === "all" ? "all" : date as EncounterFilterDate, filter: filter === "all" ? "all" : filter as EncounterFilter, status: status === "all" ? "all" : status as EncounterFilterStatus, search: debouncedSearchValue
    //             }),
    //         })
    //     }
    // }, [data, isPlaceholderData, debouncedSearchValue, filter, date, status, tab, pageSize, skip])

    // Defensive fallback for data
    const payments = Array.isArray(data?.payments) ? data.payments : [];
    const total = typeof data?.totalCount === "number" ? data.totalCount : 0;

    // Reset to first page when filters/search change
    useEffect(() => {
        setCurrentPage(1);
    }, [tab, date, status, debouncedSearchValue]);

    const totalPages = Math.ceil(total / pageSize) || 1;
    const startIndex = total === 0 ? 0 : (skip + 1);
    const endIndex = total === 0 ? 0 : (skip + payments.length);

    const handleTabChange = (tab: string) => {
        navigate(paymentsLinkOptions(program, tab))
        // setSelectedPayment(null);
        setCurrentPage(1); // Reset to first page on tab change
    };

    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    return (
        <section
            aria-label="PaymentsPage"
            className="flex flex-col w-screen min-h-screen md:max-w-[1440px] sm:w-full px-3 sm:px-4 md:px-6 lg:px-12 py-5 md:py-10 gap-3 md:gap-5"
        >
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold">
                {isMobile ? "Payments" : "Payments history"}
            </h2>

            <div className="w-full">
                <PaymentFilters
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    setDate={setDate}
                    date={date}
                    status={status}
                    setStatus={setStatus}
                />
            </div>

            <Tabs
                defaultValue="all"
                value={tab}
                onValueChange={handleTabChange}
            >
                <div className="w-full flex overflow-x-auto pb-1 sm:pb-2">
                    <TabsList className="flex border-b no-scrollbar bg-transparent px-0 py-0 sm:py-1 whitespace-nowrap">
                        <TabsTrigger
                            value="all"
                            className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                        >
                            All
                        </TabsTrigger>
                        <TabsTrigger
                            value="medications"
                            className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                        >
                            Medications
                        </TabsTrigger>
                        <TabsTrigger
                            value="consultations"
                            className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                        >
                            Consultations
                        </TabsTrigger>
                        <TabsTrigger
                            value="appointments"
                            className="tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2"
                        >
                            Appointments
                        </TabsTrigger>
                    </TabsList>
                </div>

                {tab && (<TabsContent value={tab} className="mt-2 sm:mt-4 w-full sm:w-full">
                    <div className="flex flex-col w-full gap-[32px] items-start">

                        {isLoading ? (
                            <EncounterListSkeleton />
                        ) : payments.length > 0 ? (
                            <PaymentHistoryTable data={data?.payments ?? []} />
                        ) : (
                            <div className="flex flex-col gap-3 justify-center items-center w-full">
                                <h2 className="text-lg font-medium">No  {tab === "All" ? "" : `${toTitleCase(tab)}`} payments found</h2>
                                <p className="text-sm text-gray-500">When payments are available, they will appear here</p>
                            </div>
                        )}
                    </div>
                </TabsContent>)}
            </Tabs>

            {/* Pagination UI */}
            <div className="flex items-center text-xs sm:text-base justify-between border-t border-gray-200 px-2 py-3 mt-4">
                <div className="text-gray-700">
                    Showing <span className="font-medium">{startIndex}</span> to{" "}
                    <span className="font-medium">{endIndex}</span> of{" "}
                    <span className="font-medium">{(data?.totalCount ?? 0)}</span> Payments
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="icon"
                        disabled={currentPage === 1 || isFetching}
                        onClick={() => handlePageChange(currentPage - 1)}
                        className="h-8 w-8 rounded cursor-pointer"
                        aria-label="Previous page"
                    >
                        <ChevronLeft className="h-4 w-4 cursor-pointer" />
                    </Button>
                    <span className="font-medium">
                        Page {currentPage} of {totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="icon"
                        disabled={currentPage === totalPages || isFetching}
                        onClick={() => handlePageChange(currentPage + 1)}
                        className="h-8 w-8 rounded cursor-pointer"
                        aria-label="Next page"
                    >
                        <ChevronRight className="h-4 w-4 cursor-pointer" />
                    </Button>
                </div>
            </div>
        </section>
    );
}